import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Check, 
  Shield, 
  Zap,
  Crown,
  Loader2,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [stripeMode, setStripeMode] = useState(null);

  useEffect(() => {
    fetchPlans();
    fetchStripeMode();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeMode = async () => {
    try {
      const response = await axios.get(`${API}/payments/mode`);
      setStripeMode(response.data);
    } catch (error) {
      console.error('Failed to fetch Stripe mode:', error);
    }
  };

  const handleUpgrade = async (planId) => {
    if (!user) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }
    if (user.is_premium) {
      toast.info('You are already a premium member!');
      return;
    }
    if (planId === 'free') {
      return;
    }

    setProcessingPlan(planId);
    try {
      // Get the current origin for success/cancel URLs
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/payments/checkout`, {
        plan_id: planId,
        origin_url: originUrl
      });
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setProcessingPlan(null);
    }
  };

  const getPlanIcon = (name) => {
    switch (name) {
      case 'Free': return Shield;
      case 'Premium': return Zap;
      case 'Premium Annual': return Crown;
      default: return Shield;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-16" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Test Mode Banner */}
        {stripeMode?.is_test && (
          <div className="mb-8 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-center" data-testid="test-mode-banner">
            <p className="text-yellow-400 font-medium">
              🧪 Test Mode Active - Use card <span className="font-mono bg-yellow-500/20 px-2 py-0.5 rounded">4242 4242 4242 4242</span> with any future date & CVC
            </p>
          </div>
        )}
        
        {/* Header */}
        <div className="text-center mb-16">
          <span className="font-mono text-sm text-[#00FF94] uppercase tracking-widest mb-4 block">
            Pricing
          </span>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mb-4">
            Choose Your Protection Level
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Start free and upgrade when you need more features. All plans include our core privacy tools.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan) => {
            const Icon = getPlanIcon(plan.name);
            const isPopular = plan.name === 'Premium';
            const isCurrent = (plan.name === 'Free' && !user?.is_premium) || 
                             (plan.name !== 'Free' && user?.is_premium);
            const isProcessing = processingPlan === plan.id;
            
            return (
              <Card 
                key={plan.id}
                className={`relative bg-[#0a0a0a] border-[#262626] overflow-hidden ${isPopular ? 'border-[#00FF94]/50 scale-105' : ''}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-[#00FF94] text-black text-xs font-bold px-3 py-1">
                    POPULAR
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#00FF94]" />
                  </div>
                  <CardTitle className="text-white text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="font-heading font-bold text-4xl text-white">
                      ${plan.price}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-gray-500 ml-1">/{plan.interval}</span>
                    )}
                  </div>
                  {plan.name === 'Premium Annual' && (
                    <p className="text-[#00FF94] text-sm mt-2">Save 33% vs monthly</p>
                  )}
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#00FF94] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-400 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {plan.name === 'Free' ? (
                    isCurrent ? (
                      <Button disabled className="w-full bg-[#171717] text-gray-500 cursor-default">
                        Current Plan
                      </Button>
                    ) : (
                      <Link to="/register">
                        <Button className="w-full btn-secondary" data-testid="get-started-free">
                          Get Started Free
                        </Button>
                      </Link>
                    )
                  ) : isCurrent ? (
                    <Button disabled className="w-full bg-[#171717] text-gray-500 cursor-default">
                      Current Plan
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={isProcessing}
                      className={`w-full ${isPopular ? 'btn-primary' : 'btn-secondary'}`}
                      data-testid={`upgrade-btn-${plan.id}`}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          {user ? 'Upgrade Now' : 'Get Started'}
                        </>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Security Note */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0a0a0a] border border-[#262626] rounded-full">
            <Shield className="w-4 h-4 text-[#00FF94]" />
            <span className="text-sm text-gray-400">Secure payments powered by Stripe</span>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-20">
          <h2 className="font-heading font-bold text-2xl text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: 'Can I cancel anytime?',
                a: 'Yes, you can cancel your subscription at any time. Your premium features will remain active until the end of your billing period.'
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit cards (Visa, Mastercard, American Express) through our secure Stripe payment system.'
              },
              {
                q: 'Is there a refund policy?',
                a: 'Yes, we offer a 30-day money-back guarantee if you\'re not satisfied with Premium.'
              },
              {
                q: 'Do you store my payment data?',
                a: 'No. All payment processing is handled securely by Stripe. We never store your card details.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-[#0a0a0a] border border-[#262626] rounded-lg p-6">
                <h3 className="font-medium text-white mb-2">{faq.q}</h3>
                <p className="text-gray-500 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-500 mb-4">
            Have questions? <a href="/contact" className="text-[#00FF94] hover:underline">Contact us</a>
          </p>
        </div>
      </div>
    </div>
  );
}
